import React, {useEffect, useMemo, useState} from "react";
import {collection, doc, getDoc, getDocs, query, where} from "firebase/firestore";
import {Link, useParams} from "react-router-dom";
import {db} from "../../../firebase/FirebaseProvider";
import {Location, Stay, User} from "../../types/Types";
import UserDetailsV2 from "../UserDetailsV2";
import PageTracking from "../../auth/PageTracking";
import DateTime from "../../date/DateTime";
import {getCountryFlag} from "../../util/Util";
import {V2GlassChip, V2GlassPanel, V2GlassSurface} from "../../shared/v2/V2Glass";
import V2YearPicker from "../../shared/v2/V2YearPicker";
import "../UserDetailsV2.css";

interface Props {
    locations: Array<Location> | undefined;
    currentUser: User | undefined;
}

function getStayYear(value: any): number {
    if (value?.seconds) {
        return new Date(value.seconds * 1000).getFullYear();
    }
    if (value?.toDate) {
        return value.toDate().getFullYear();
    }
    return new Date(value).getFullYear();
}

function getStayTimestamp(value: any): number {
    if (value?.seconds) {
        return value.seconds * 1000;
    }
    if (value?.toDate) {
        return value.toDate().getTime();
    }
    return new Date(value).getTime();
}

function UserStays(props: Props) {
    const {id} = useParams<{id: string}>();
    const [user, setUser] = useState<User | undefined>(undefined);
    const [stays, setStays] = useState<Array<Stay> | undefined>(undefined);
    const [year, setYear] = useState<string>("1");

    useEffect(() => {
        if (!id) {
            return;
        }

        (async () => {
            const userSnapshot = await getDoc(doc(db, "users", id));
            if (userSnapshot.exists()) {
                setUser({...userSnapshot.data() as User, id: userSnapshot.id});
            }
        })().catch((error) => console.error("Failed to load stay user", error));
    }, [id]);

    useEffect(() => {
        if (!id) {
            return;
        }

        (async () => {
            const staySnapshot = await getDocs(query(
                collection(db, "stays"),
                where("userId", "==", id),
                where("stayed", "==", true),
            ));
            const loadedStays = staySnapshot.docs
                .map((stayDoc) => stayDoc.data() as Stay)
                .sort((first, second) => getStayTimestamp(second.lastModified) - getStayTimestamp(first.lastModified));
            setStays(loadedStays);
        })().catch((error) => {
            console.error("Failed to load user stays", error);
            setStays([]);
        });
    }, [id]);

    const locationsMap = useMemo(
        () => new Map((props.locations || []).map((location) => [location.id, location])),
        [props.locations],
    );
    const availableYears = useMemo(() => {
        if (!stays) {
            return [];
        }

        return Array.from(new Set(
            stays
                .filter((stay) => locationsMap.get(stay.locationId)?.hotel)
                .map((stay) => getStayYear(stay.lastModified)),
        ));
    }, [locationsMap, stays]);
    const displayedStays = useMemo(() => {
        if (!stays) {
            return undefined;
        }

        return stays.filter((stay) => {
            const location = locationsMap.get(stay.locationId);
            if (!location || !location.hotel || (location.enabled !== undefined && !location.enabled)) {
                return false;
            }
            return year === "1" || getStayYear(stay.lastModified) === Number(year);
        });
    }, [locationsMap, stays, year]);
    const openHotelStayCount = useMemo(() => {
        if (!displayedStays) {
            return 0;
        }

        return new Set(
            displayedStays
                .filter((stay) => {
                    const location = locationsMap.get(stay.locationId);
                    return !!location
                        && !location.pubIsClosed
                        && !location.pubIsTemporaryClosed;
                })
                .map((stay) => stay.locationId),
        ).size;
    }, [displayedStays, locationsMap]);
    const openHotelCount = useMemo(() => (
        (props.locations || []).filter((location) => (
            location.hotel
            && !location.pubIsClosed
            && !location.pubIsTemporaryClosed
            && (location.enabled === undefined || location.enabled)
        )).length
    ), [props.locations]);
    const openHotelStayPercentage = openHotelCount > 0
        ? Math.round((openHotelStayCount / openHotelCount) * 100)
        : 0;

    const detailHeader = (
        <div className="user-v2-subpage-header-content">
            <div className="user-v2-subpage-header-stats">
                <span>User has stayed at <b>{openHotelStayPercentage}%</b> of open hotels</span>
            </div>
            <V2YearPicker value={year} onChange={setYear} availableYears={availableYears} ariaLabel="Select stay year" />
        </div>
    );

    return (
        <>
            <PageTracking />
            <UserDetailsV2
                currentUser={props.currentUser}
                user={user}
                locations={props.locations}
                detailHeader={detailHeader}
                detailStatValues={{Stays: displayedStays?.length || 0}}
            />
            <V2GlassPanel className="user-v2-visits-list-card v2-glass-list-panel">
                {displayedStays && displayedStays.map((stay, index) => {
                    const location = locationsMap.get(stay.locationId);
                    if (!location) {
                        return null;
                    }

                    return (
                        <V2GlassSurface key={`${stay.locationId}-${index}`} className="user-v2-visit-row v2-glass-list-row">
                            <div className="user-v2-visit-main">
                                <div className="user-v2-visit-title-line">
                                    <Link to={`/location/${location.id}/comments`} className="user-v2-location-name-link">
                                        <V2GlassChip className="v2-glass-location-chip">{location.name}</V2GlassChip>
                                    </Link>
                                </div>
                                <div className="user-v2-visit-meta">
                                    <span className="user-v2-visit-country">{getCountryFlag(location.country || "")}</span>
                                    <span className="user-v2-visit-address">{location.address}</span>
                                </div>
                            </div>
                            <V2GlassChip className="user-v2-visit-date-chip">
                                <DateTime date={stay.lastModified} />
                            </V2GlassChip>
                        </V2GlassSurface>
                    );
                })}
                {displayedStays && displayedStays.length === 0 && <div className="user-v2-empty-list">No stays for this year</div>}
                {!displayedStays && <div className="user-v2-empty-list">Loading...</div>}
            </V2GlassPanel>
        </>
    );
}

export default UserStays;
